/**
 * Fountain import parser — PURE module (no React, no Lexical), mirroring
 * model.js's / transitions.js's discipline: fully unit-testable, dependency-
 * free except for model.js/transitions.js (also pure).
 *
 * Design approved 2026-07-14 — full mapping table in the PR description.
 * Client-side only: the backend never sees Fountain syntax. Import produces
 * a normal elements array that goes through the EXISTING validated
 * PUT-with-rev save path (saveClient.js) unchanged — no new backend surface,
 * no new matched-pair sync risk.
 *
 * Contract: parseFountain(source) -> { elements, report }
 *   - Throws ScreenplayParseError (model.js) if the resulting document would
 *     exceed ELEMENT_CAP / TOTAL_TEXT_CAP / ELEMENT_TEXT_CAP, or if a single
 *     element's id would exceed ELEMENT_ID_CAP (can't happen with minted
 *     ids, kept as a defense-in-depth belt-and-suspenders check) — NEVER
 *     truncates.
 *   - report: { kept, converted, stripped, titlePage } — drives the
 *     pre-commit summary UI. Nothing is silently dropped: every construct
 *     this parser doesn't store is counted and named in `stripped`.
 *
 * ⚠ Deliberate deviation from "reference" Fountain parsers (fountain.io,
 * Screenplain, etc.): those swallow an UNTERMINATED /* boneyard or [[ note
 * marker to end-of-file. We do NOT — an unterminated marker is far more
 * likely a typo than an intent to comment out the rest of the script, and
 * silently discarding 90% of a writer's manuscript because of one stray
 * `/*` fails "nothing silently dropped" far worse than leaving a stray
 * marker character as literal text. Reported either way.
 */
import {
  ScreenplayParseError,
  createElement,
  ELEMENT_CAP,
  ELEMENT_TEXT_CAP,
  TOTAL_TEXT_CAP,
} from './model';
import { HEADING_PREFIX_RE, completeSceneHeadingPrefix, COMMON_TRANSITIONS } from './transitions';

/** Raw source-text ceiling, checked before any parsing work begins — bounds
 * worst-case parse time on a hostile/huge paste (separate from the caps on
 * the RESULT, which bound the saved document). */
export const MAX_SOURCE_BYTES = 5 * 1024 * 1024; // 5MB

function freshReport() {
  return {
    kept: {
      scene_heading: 0,
      action: 0,
      character: 0,
      parenthetical: 0,
      dialogue: 0,
      transition: 0,
      shot: 0,
    },
    converted: {
      headingsNormalized: 0,
      forcedSceneHeadings: 0,
      forcedActions: 0,
      forcedCharacters: 0,
      forcedTransitions: 0,
      dualDialoguePairs: 0,
    },
    stripped: {
      sceneNumbers: 0,
      sections: 0,
      synopses: 0,
      notes: 0,
      notesUnterminated: 0,
      boneyard: 0,
      boneyardUnterminated: 0,
      centered: 0,
      lyrics: 0,
      pageBreaks: 0,
      emphasisMarkers: 0,
    },
    titlePage: [], // [{ key, value }] — informational only, never stored
    elementCount: 0,
    totalChars: 0,
  };
}

/* ── Preprocessing ────────────────────────────────────────────────────── */

function normalizeLineEndings(source) {
  return source.replace(/\r\n?/g, '\n');
}

function stripBom(source) {
  return source.charCodeAt(0) === 0xfeff ? source.slice(1) : source;
}

/**
 * Strip well-formed (paired) occurrences of a bracket-style construct from
 * the WHOLE source in one pass — handles both inline ("text /* note *\/
 * more text") and multi-line occurrences uniformly. Returns the text with
 * every matched pair removed, plus the count removed. Any UNMATCHED opening
 * marker left in the result is, by definition, unterminated (its partner
 * was already consumed above, or it never had one) — see module doc for why
 * we leave those as literal text rather than swallowing to end-of-file.
 */
function stripPaired(source, pairRe) {
  let count = 0;
  const text = source.replace(pairRe, (match) => {
    count += 1;
    // Preserve the LINE STRUCTURE the match spanned — replacing a multi-line
    // pair with '' would fuse the lines on either side of it, silently
    // restructuring elements (a note spanning a blank line would merge two
    // dialogue blocks; a boneyard between an action and a transition would
    // swallow the transition into the action). A blank line inside the match
    // stays a blank line (element boundary survives); a single newline stays
    // a single newline.
    if (/\n[ \t]*\n/.test(match)) return '\n\n';
    if (match.includes('\n')) return '\n';
    return '';
  });
  return { text, count };
}

function stripBoneyardAndNotes(source, report) {
  const boneyard = stripPaired(source, /\/\*[\s\S]*?\*\//g);
  report.stripped.boneyard = boneyard.count;
  const withoutBoneyard = boneyard.text;
  report.stripped.boneyardUnterminated = (withoutBoneyard.match(/\/\*/g) || []).length;

  const notes = stripPaired(withoutBoneyard, /\[\[[\s\S]*?\]\]/g);
  report.stripped.notes = notes.count;
  const withoutNotes = notes.text;
  report.stripped.notesUnterminated = (withoutNotes.match(/\[\[/g) || []).length;

  return withoutNotes;
}

/**
 * Title page: "Key: value" lines at the very top of the document, ending at
 * the first blank line. Multi-line values are indented continuation lines.
 *
 * The key must be from a KNOWN set, not "any capitalized word(s) followed
 * by a colon" — the permissive version is a real false-positive trap: a
 * screenplay that happens to open with a transition-shaped first line (e.g.
 * "CUT TO:") would otherwise be swallowed whole as a one-entry title page
 * with an empty value, silently eating real content. Reference Fountain
 * parsers vary on this; restricting to known keys is the safer choice here.
 */
const KNOWN_TITLE_KEYS = new Set([
  'title', 'credit', 'author', 'authors', 'source', 'draft date',
  'contact', 'contact info', 'notes', 'copyright', 'revision', 'draft',
]);

const TITLE_KEY_RE = /^([A-Za-z][A-Za-z0-9 ]*):\s*(.*)$/;

function matchTitleKey(line) {
  const m = TITLE_KEY_RE.exec(line);
  if (!m || !KNOWN_TITLE_KEYS.has(m[1].trim().toLowerCase())) return null;
  return m;
}

function extractTitlePage(lines, report) {
  if (lines.length === 0) return 0;
  if (!matchTitleKey(lines[0].trim())) return 0;

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === '') break; // blank line ends the title page
    const m = matchTitleKey(line);
    if (m) {
      report.titlePage.push({ key: m[1].trim(), value: m[2].trim() });
    } else if (report.titlePage.length > 0 && /^\s/.test(line)) {
      // Indented continuation of the previous key's value.
      const entry = report.titlePage[report.titlePage.length - 1];
      entry.value = `${entry.value}\n${line.trim()}`.trim();
    } else {
      break; // not indented, not a known key — title page ended without a blank line
    }
    i += 1;
  }
  return i; // number of lines consumed (caller skips past the boundary blank line too)
}

/* ── Line classification helpers ──────────────────────────────────────── */

// A-Za-z + Latin-1/Extended-A/B (À-ɏ) + Latin Extended Additional
// (U+1E00–U+1EFF). The last range is what Yorùbá (Ọ Ẹ Ṣ) and Igbo (Ị Ụ Ṅ)
// are written in: without it a cue spelled entirely in those letters — ỌṢỌ,
// ṄNAMDỊ — has no "Latin letter" to find and is read as action, not a
// character cue. Caseless scripts (Ge'ez, CJK) still correctly fall out
// below via the toUpperCase/toLowerCase test and keep needing `@`.
const HAS_LATIN_LETTER_RE = /[A-Za-zÀ-ɏḀ-ỿ]/;

/**
 * True only for lines whose case is MEANINGFULLY all-caps — i.e. the string
 * actually has case-distinguishable letters (so a caseless script like
 * Amharic/Ge'ez, where toUpperCase()/toLowerCase() are no-ops, correctly
 * never matches here — see the design-gate note on non-Latin character
 * detection; those scripts need the `@` force-cue marker).
 */
function isAllCapsLine(text) {
  if (!HAS_LATIN_LETTER_RE.test(text)) return false;
  return text === text.toUpperCase() && text !== text.toLowerCase();
}

/** Character cues don't end in sentence punctuation — a strong, low-risk
 * signal for telling "SHE WALKS DOWN THE STREET." (action) from "JOHN" (cue)
 * apart, matching how real-world Fountain tools refine the naive
 * all-caps-plus-lookahead heuristic. */
function endsLikeASentence(text) {
  return /[.!?]$/.test(text) || /\.\.\.$/.test(text);
}

function stripEmphasisMarkers(text, report) {
  let count = 0;
  const stripped = text
    .replace(/\*{1,3}([^*]+)\*{1,3}/g, (_, inner) => {
      count += 1;
      return inner;
    })
    .replace(/_([^_]+)_/g, (_, inner) => {
      count += 1;
      return inner;
    });
  report.stripped.emphasisMarkers += count;
  return stripped;
}

function stripSceneNumber(text, report) {
  const m = /^(.*?)\s*#([A-Za-z0-9.]+)#\s*$/.exec(text);
  if (!m) return text;
  report.stripped.sceneNumbers += 1;
  return m[1];
}

function isPageBreak(trimmed) {
  return /^={3,}$/.test(trimmed);
}

function isSection(trimmed) {
  return /^#{1,6}(\s|$)/.test(trimmed);
}

// Caller already dispatches isPageBreak() first, so by construction this is
// only ever reached for a '='-led line that ISN'T a page break.
function isSynopsis(trimmed) {
  return trimmed.startsWith('=');
}

/* ── Main parse ───────────────────────────────────────────────────────── */

/**
 * Parse Fountain source text into { elements, report }.
 * `elements` is a plain array of { type, text } (NOT yet ids — the caller
 * mints ids via model.js's createElement, matching how every other write
 * path produces fresh elements).
 */
export function parseFountain(source) {
  if (typeof source !== 'string') {
    throw new ScreenplayParseError('import source must be a string');
  }
  // Byte length, not .length (UTF-16 units) — mirrors the keepalive
  // byte-vs-length lesson from Phase 3 review F3. Multi-byte scripts
  // (Amharic, emoji) would under-count against a raw .length check.
  const byteLength = new TextEncoder().encode(source).length;
  if (byteLength > MAX_SOURCE_BYTES) {
    throw new ScreenplayParseError(
      `file is ${byteLength} bytes — the import limit is ${MAX_SOURCE_BYTES} bytes`
    );
  }

  const report = freshReport();

  let text = stripBom(source);
  text = normalizeLineEndings(text);
  text = stripBoneyardAndNotes(text, report);

  const lines = text.split('\n');
  const titleLineCount = extractTitlePage(lines, report);
  let i = titleLineCount;
  // Skip the blank line that terminated the title page, if any.
  if (titleLineCount > 0 && lines[i]?.trim() === '') i += 1;

  const elements = [];
  let totalChars = 0;

  // The server's total-text budget counts text PLUS each element's id
  // (len("el_" + 16 hex) = 19) — see _validate_screenplay in server.py.
  // The client cap check must use the same arithmetic, or a document in the
  // 19-chars-per-element gap passes here, shows a summary, and then 413s at
  // save — exactly the late failure this module exists to prevent.
  const ID_CHARS_PER_ELEMENT = 19;

  function checkCaps() {
    if (elements.length > ELEMENT_CAP) {
      throw new ScreenplayParseError(
        `import produced ${elements.length} elements — the cap is ${ELEMENT_CAP}`
      );
    }
    const budget = totalChars + elements.length * ID_CHARS_PER_ELEMENT;
    if (budget > TOTAL_TEXT_CAP) {
      throw new ScreenplayParseError(
        `import produced ${budget} characters of text and ids — the cap is ${TOTAL_TEXT_CAP}`
      );
    }
  }

  function push(type, rawText) {
    if (rawText.length > ELEMENT_TEXT_CAP) {
      throw new ScreenplayParseError(
        `a ${type} element is ${rawText.length} characters — the cap is ${ELEMENT_TEXT_CAP}`
      );
    }
    elements.push({ type, text: rawText });
    report.kept[type] += 1;
    totalChars += rawText.length;
    checkCaps();
  }

  const n = lines.length;

  function hasNonBlankFollowing(idx) {
    return idx + 1 < n && lines[idx + 1].trim() !== '';
  }

  /** Consume a character's parenthetical/dialogue block starting at idx
   * (the line AFTER the cue). Joins consecutive dialogue lines into one
   * element (embedded \n, matching how our own editor already stores
   * Shift+Enter line breaks); each (...) line is its own parenthetical. */
  function consumeDialogueBlock(startIdx) {
    let idx = startIdx;
    let buffer = [];
    const flush = () => {
      if (buffer.length > 0) {
        push('dialogue', stripEmphasisMarkers(buffer.join('\n'), report));
        buffer = [];
      }
    };
    while (idx < n && lines[idx].trim() !== '') {
      const line = lines[idx].trim();
      if (line.startsWith('(') && line.endsWith(')')) {
        flush();
        // Same emphasis treatment as the dialogue around it — "(*sotto*)"
        // keeping its asterisks while adjacent dialogue is stripped would be
        // an uncounted asymmetry.
        push('parenthetical', stripEmphasisMarkers(line, report));
      } else {
        buffer.push(line);
      }
      idx += 1;
    }
    flush();
    return idx;
  }

  while (i < n) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    if (trimmed === '') {
      i += 1;
      continue;
    }

    if (isPageBreak(trimmed)) {
      report.stripped.pageBreaks += 1;
      i += 1;
      continue;
    }

    if (isSection(trimmed)) {
      report.stripped.sections += 1;
      i += 1;
      continue;
    }

    if (isSynopsis(trimmed)) {
      report.stripped.synopses += 1;
      i += 1;
      continue;
    }

    // Forced scene heading: leading '.' not followed by another '.' (avoids
    // ellipsis false-positives).
    if (trimmed.startsWith('.') && trimmed[1] !== '.') {
      let headingText = trimmed.slice(1);
      headingText = stripSceneNumber(headingText, report);
      const canon = completeSceneHeadingPrefix(headingText);
      if (canon) {
        headingText = canon.text;
        report.converted.headingsNormalized += 1;
      }
      push('scene_heading', headingText);
      report.converted.forcedSceneHeadings += 1;
      i += 1;
      continue;
    }

    // Standard scene heading (INT./EXT./EST./I/E., case-insensitive).
    if (HEADING_PREFIX_RE.test(trimmed)) {
      let headingText = stripSceneNumber(trimmed, report);
      const canon = completeSceneHeadingPrefix(headingText);
      if (canon) {
        headingText = canon.text;
        report.converted.headingsNormalized += 1;
      }
      push('scene_heading', headingText);
      i += 1;
      continue;
    }

    // Forced transition: leading '>' WITHOUT a trailing '<' (that combo is
    // centered text, handled next).
    if (trimmed.startsWith('>') && !trimmed.endsWith('<')) {
      push('transition', trimmed.slice(1).trim());
      report.converted.forcedTransitions += 1;
      i += 1;
      continue;
    }

    // Centered text: wrapped '>' ... '<' — no equivalent type, nearest is
    // action; centering itself is lost (reported).
    if (trimmed.startsWith('>') && trimmed.endsWith('<')) {
      const inner = trimmed.slice(1, -1).trim();
      push('action', stripEmphasisMarkers(inner, report));
      report.stripped.centered += 1;
      i += 1;
      continue;
    }

    // Forced character: leading '@' (permits mixed-case names). Explicit
    // markers ALWAYS outrank the ambiguous ALL-CAPS heuristics below — an
    // author who forces something has already resolved the ambiguity.
    if (trimmed.startsWith('@')) {
      let name = trimmed.slice(1);
      const isDual = name.endsWith('^');
      if (isDual) {
        name = name.slice(0, -1).trim();
        report.converted.dualDialoguePairs += 1;
      }
      push('character', name);
      report.converted.forcedCharacters += 1;
      i = consumeDialogueBlock(i + 1);
      continue;
    }

    // Forced action: leading '!'. Must be checked BEFORE the standard
    // character-cue heuristic below — an all-caps line forced with '!' (the
    // documented escape hatch for the residual ambiguity) would otherwise
    // still satisfy the heuristic and get misread as a cue.
    if (trimmed.startsWith('!')) {
      push('action', stripEmphasisMarkers(trimmed.slice(1), report));
      report.converted.forcedActions += 1;
      i += 1;
      continue;
    }

    // Lyrics: leading '~' — no equivalent type, nearest is action; styling
    // lost (reported).
    if (trimmed.startsWith('~')) {
      push('action', stripEmphasisMarkers(trimmed.slice(1), report));
      report.stripped.lyrics += 1;
      i += 1;
      continue;
    }

    // Standard transition: ALL CAPS, ends in "TO:" or a known standalone
    // term (FADE OUT., etc. — reuses the SAME list the typing UI suggests).
    if (
      isAllCapsLine(trimmed) &&
      (trimmed.endsWith('TO:') || COMMON_TRANSITIONS.includes(trimmed))
    ) {
      push('transition', trimmed);
      i += 1;
      continue;
    }

    // Standard character cue: ALL CAPS + a non-blank line follows + doesn't
    // read like a sentence (see endsLikeASentence doc comment — this is the
    // refinement that keeps an all-caps-prose file from being shredded into
    // fake character cues).
    if (
      isAllCapsLine(trimmed) &&
      hasNonBlankFollowing(i) &&
      !endsLikeASentence(trimmed)
    ) {
      let name = trimmed;
      const isDual = name.endsWith('^');
      if (isDual) {
        name = name.slice(0, -1).trim();
        report.converted.dualDialoguePairs += 1;
      }
      push('character', name);
      i = consumeDialogueBlock(i + 1);
      continue;
    }

    // Default: action, possibly multi-line (joined until the next blank
    // line, embedded \n — same join rule as dialogue).
    {
      const buffer = [trimmed];
      let idx = i + 1;
      while (idx < n && lines[idx].trim() !== '') {
        buffer.push(lines[idx].trim());
        idx += 1;
      }
      push('action', stripEmphasisMarkers(buffer.join('\n'), report));
      i = idx;
    }
  }

  report.elementCount = elements.length;
  report.totalChars = totalChars;

  return { elements, report };
}

/**
 * Convenience: parse and immediately mint fresh model elements (ids via
 * model.js's createElement — never reuse anything from the source). Throws
 * the same ScreenplayParseError as parseFountain on any cap violation.
 */
export function fountainToElements(source) {
  const { elements, report } = parseFountain(source);
  return { elements: elements.map((el) => createElement(el.type, el.text)), report };
}
